import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { mockContactSubmit } from '../mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to 10 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitMessage('Please fill in all required fields');
      return;
    }

    if (formData.phone.length !== 10) {
      setSubmitMessage('Phone number must be 10 digits');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await mockContactSubmit({
        ...formData,
        fullPhone: `${formData.countryCode} ${formData.phone}`
      });
      
      if (result.success) {
        setSubmitMessage(result.message);
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Office Address",
      details: ["600 W Broadway Suite 700", "San Diego, CA 92101"],
      link: null
    },
    {
      icon: Phone,
      title: "Phone Number",
      details: ["+1 (123) 123-4567"],
      link: "tel:+11231234567"
    },
    {
      icon: Mail,
      title: "Email Address",
      details: ["info@trademarktexticon.com"],
      link: "mailto:info@trademarktexticon.com"
    }
  ];

  return (
    <section className="contact-section" id="contact">
      <div className="section-container">
        <div className="section-header-center">
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">
            Contact Our
            <span className="title-highlight"> Trademark Experts</span>
          </h2>
          <p className="section-description">
            Ready to protect your brand? Reach out to our team for a free consultation. 
            We're here to answer your questions and guide you through the trademark 
            registration process.
          </p>
        </div>

        <div className="contact-grid">
          {/* Contact Info Cards */}
          <div className="contact-info-section">
            <h3 className="contact-info-title">Contact Information</h3>
            <div className="contact-info-cards">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div key={index} className="contact-info-card">
                    <div className="contact-info-icon">
                      <Icon size={24} />
                    </div>
                    <div className="contact-info-content">
                      <h4 className="contact-info-label">{info.title}</h4>
                      {info.link ? (
                        <a href={info.link} className="contact-info-link">
                          {info.details.map((detail, idx) => (
                            <div key={idx}>{detail}</div>
                          ))}
                        </a>
                      ) : (
                        <div className="contact-info-text">
                          {info.details.map((detail, idx) => (
                            <div key={idx}>{detail}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="contact-hours">
              <h4 className="contact-hours-title">Business Hours</h4>
              <div className="contact-hours-list">
                <div className="hours-item">
                  <span className="hours-day">Monday - Friday:</span>
                  <span className="hours-time">9:00 AM - 6:00 PM PST</span>
                </div>
                <div className="hours-item">
                  <span className="hours-day">Saturday:</span>
                  <span className="hours-time">10:00 AM - 4:00 PM PST</span>
                </div>
                <div className="hours-item">
                  <span className="hours-day">Sunday:</span>
                  <span className="hours-time">Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Contact Number <span className="required">*</span>
                </label>
                <div className="phone-input-group">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="country-code-select"
                  >
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+91">+91 (IN)</option>
                    <option value="+61">+61 (AU)</option>
                    <option value="+86">+86 (CN)</option>
                  </select>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="1234567890"
                    maxLength={10}
                    required
                    className="phone-input"
                  />
                </div>
                <span className="form-helper">10 digits, numbers only</span>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your trademark needs..."
                  rows={5}
                  className="form-textarea"
                />
              </div>

              {submitMessage && (
                <div className={`form-message ${submitMessage.includes('success') || submitMessage.includes('thank') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}

              <Button 
                type="submit" 
                className="contact-submit-button" 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <Send size={20} />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
